import React, { useState, useEffect } from 'react';
import { X, Calendar, Euro, Home, Users, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const CreateDealModal = ({ open, onClose, propertyId, onSuccess }) => {
  const [loading, setLoading] = useState(false);
  const [roomTypes, setRoomTypes] = useState([]);
  const [formData, setFormData] = useState({
    room_type_name: '',
    check_in: '',
    check_out: '',
    original_price: '',
    discounted_price: '',
    available_units: 1,
    description: ''
  });

  useEffect(() => {
    if (open && propertyId) {
      fetchRoomTypes();
    }
  }, [open, propertyId]);

  const fetchRoomTypes = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/properties/${propertyId}/room-types`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setRoomTypes(response.data || []);
    } catch (error) {
      console.error('Error fetching room types:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${BACKEND_URL}/api/deals`,
        {
          property_id: propertyId,
          ...formData,
          original_price: parseFloat(formData.original_price),
          discounted_price: parseFloat(formData.discounted_price),
          available_units: parseInt(formData.available_units)
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success('🎉 Deal last-minute creato con successo!');
      onSuccess();
      onClose();
      setFormData({
        room_type_name: '',
        check_in: '',
        check_out: '',
        original_price: '',
        discounted_price: '',
        available_units: 1,
        description: ''
      });
    } catch (error) {
      console.error('Error creating deal:', error);
      toast.error(error.response?.data?.detail || 'Errore nella creazione del deal');
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-gradient-to-r from-orange-600 to-orange-700 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Crea Deal Last-Minute</h2>
            <p className="text-orange-100 text-sm mt-1">Offri uno sconto speciale per prenotazioni urgenti</p>
          </div>
          <button onClick={onClose} className="text-white hover:bg-white/20 rounded-full p-2 transition-colors">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Room Type */}
          <div>
            <Label htmlFor="room_type" className="flex items-center gap-2 mb-2">
              <Home className="w-4 h-4 text-orange-600" />
              Tipo Camera *
            </Label>
            <select
              id="room_type"
              value={formData.room_type_name}
              onChange={(e) => setFormData({ ...formData, room_type_name: e.target.value })}
              className="w-full h-10 px-3 rounded-md border border-slate-200 bg-white focus:border-orange-500 focus:ring-2 focus:ring-orange-500/20 outline-none"
              required
            >
              <option value="">Seleziona camera</option>
              {roomTypes.map((room) => (
                <option key={room.id} value={room.name}>
                  {room.name} - Capacità: {room.capacity}
                </option>
              ))}
            </select>
          </div>

          {/* Dates */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="check_in" className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-orange-600" />
                Check-in *
              </Label>
              <Input
                id="check_in"
                type="date"
                value={formData.check_in}
                onChange={(e) => setFormData({ ...formData, check_in: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="check_out" className="flex items-center gap-2 mb-2">
                <Calendar className="w-4 h-4 text-orange-600" />
                Check-out *
              </Label>
              <Input
                id="check_out"
                type="date"
                value={formData.check_out}
                onChange={(e) => setFormData({ ...formData, check_out: e.target.value })}
                required
              />
            </div>
          </div>

          {/* Prices */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="original_price" className="flex items-center gap-2 mb-2">
                <Euro className="w-4 h-4 text-orange-600" />
                Prezzo Originale *
              </Label>
              <Input
                id="original_price"
                type="number"
                min="1"
                step="0.01"
                placeholder="120.00"
                value={formData.original_price}
                onChange={(e) => setFormData({ ...formData, original_price: e.target.value })}
                required
              />
            </div>
            <div>
              <Label htmlFor="discounted_price" className="flex items-center gap-2 mb-2">
                <Euro className="w-4 h-4 text-emerald-600" />
                Prezzo Scontato *
              </Label>
              <Input
                id="discounted_price"
                type="number"
                min="1"
                step="0.01"
                placeholder="85.00"
                value={formData.discounted_price}
                onChange={(e) => setFormData({ ...formData, discounted_price: e.target.value })}
                required
              />
              {formData.original_price && formData.discounted_price && (
                <p className="text-xs text-emerald-600 mt-1">
                  Sconto: {Math.round((1 - formData.discounted_price / formData.original_price) * 100)}%
                </p>
              )}
            </div>
          </div>

          {/* Available Units */}
          <div>
            <Label htmlFor="available_units" className="flex items-center gap-2 mb-2">
              <Users className="w-4 h-4 text-orange-600" />
              Unità Disponibili *
            </Label>
            <Input
              id="available_units"
              type="number"
              min="1"
              max="50"
              value={formData.available_units}
              onChange={(e) => setFormData({ ...formData, available_units: e.target.value })}
              required
            />
          </div>

          {/* Description */}
          <div>
            <Label htmlFor="description" className="mb-2 block">
              Descrizione Deal (opzionale)
            </Label>
            <Textarea
              id="description"
              placeholder="Weekend romantico con colazione inclusa!"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows={3}
            />
          </div>

          {/* Actions */}
          <div className="flex gap-3 pt-4 border-t">
            <Button
              type="button"
              variant="outline"
              onClick={onClose}
              className="flex-1"
              disabled={loading}
            >
              Annulla
            </Button>
            <Button
              type="submit"
              className="flex-1 bg-gradient-to-r from-orange-600 to-orange-700 hover:from-orange-700 hover:to-orange-800"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Creazione...
                </>
              ) : (
                'Crea Deal'
              )}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateDealModal;